<template>
  <div
    :class="[
      'flex items-center py-3 border-b',
      dataFormat === 'distribution' ? 'pl-6' : 'px-6'
    ]"
    :style="{
      width:
        dataFormat === 'distribution' && tasks
          ? tasks.length < 8
            ? '100%'
            : tasks.length >= 9 && tasks.length < 10
            ? 'calc(100vw - 27rem)'
            : tasks.length >= 10 && tasks.length < 11
            ? 'calc(100vw - 20rem)'
            : 'calc(100vw - 2rem)'
          : ''
    }"
  >
    <div class="flex items-center flex-shrink-0 w-32 px-2 bg-pure-white">
      <BlTextBody1 :text="getDateWithoutTimezone(entry.recorded_at)" />
    </div>

    <!-- Loop through task -->
    <div
      v-if="dataFormat !== 'distribution' && dataFormat !== 'cumulative'"
      class="flex flex-shrink-0"
    >
      <div
        v-for="task in getFilteredTasks()"
        :key="task.id"
        class="flex items-center justify-center flex-shrink-0 w-16 px-2 bg-pure-white"
      >
        <div
          class="flex items-center justify-center flex-shrink-0 w-12 h-8"
          :class="{ 'cursor-pointer': !taskTarget }"
          :style="{
            backgroundColor: getAverageScore(task) ? task.color : 'transparent'
          }"
          @mouseover="!taskTarget && hoverDetail(task.id)"
          @mouseleave="!taskTarget && hoverDetail(null)"
          @click="!taskTarget && selectTask(task)"
        >
          <div v-if="taskTarget">
            <BlTextBody1
              :text="getAverageScore(task)"
              :style="{
                color: getAverageScore(task) ? '#FFFFFF' : '#A0A0A0'
              }"
            />
          </div>
          <div v-else>
            <BlTextBody1
              v-if="!isTaskHovered(task.id) && !taskTarget"
              :text="getAverageScore(task)"
              :style="{
                color: getAverageScore(task) ? '#FFFFFF' : '#A0A0A0'
              }"
            />
            <BlIcon
              v-else-if="isTaskHovered(task.id) && !taskTarget"
              icon="square-up"
              color="white"
              class="w-5 h-5"
            />
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="dataFormat === 'cumulative' || taskTarget"
      class="flex flex-shrink-0"
    >
      <div
        v-for="pb in problemBehaviors"
        :key="pb.id"
        class="flex items-center justify-center flex-shrink-0 w-16 px-2 bg-pure-white"
      >
        <div
          v-if="getRecordProblemBehaviors(pb.id) !== 0"
          class="flex items-center justify-center flex-shrink-0 w-12 h-8 cursor-pointer"
          :style="{ backgroundColor: pb.color }"
        >
          <BlTextBody1
            :text="getRecordProblemBehaviors(pb.id)"
            :style="{ color: '#FFFFFF' }"
          />
        </div>
      </div>
    </div>

    <!-- Distribution layout -->
    <div v-if="dataFormat === 'distribution'" class="flex flex-col w-full">
      <!-- For each problem behavior -->
      <div
        v-for="(pb, index) in problemBehaviors"
        :key="pb.id"
        class="flex items-center w-full"
        :class="{
          'pt-3': index !== 0,
          'border-b pb-3': index !== problemBehaviors.length - 1
        }"
      >
        <!-- Loop through tasks for this problem behavior -->
        <div class="flex flex-shrink-0">
          <div
            v-for="task in tasks"
            :key="task.id"
            class="flex items-center justify-center flex-shrink-0 w-24 px-2"
          >
            <div
              class="flex items-center justify-center flex-shrink-0 w-12 h-8 cursor-pointer"
              :style="{
                backgroundColor: hasTaskProblemBehavior(task.id, pb.id)
                  ? pb.color
                  : 'transparent'
              }"
            >
              <BlTextBody1
                :text="getTaskProblemBehaviorCount(task.id, pb.id)"
                :style="{
                  color: hasTaskProblemBehavior(task.id, pb.id)
                    ? '#FFFFFF'
                    : 'transparent'
                }"
              />
            </div>
          </div>
        </div>

        <!-- Problem behavior code -->
        <div class="flex items-center justify-center flex-shrink-0 w-40 px-2">
          <BlTextBody1 :title="pb.code" :text="pb.code" />
        </div>
      </div>
    </div>

    <!-- Rest of the component structure remains the same -->
    <div
      v-if="dataFormat !== 'distribution'"
      class="flex items-center flex-shrink-0 w-32 px-2 bg-pure-white"
    >
      <!-- User info display -->
      <BlTextBody1
        v-if="!entry.session || !entry.session.user"
        title="Deleted user"
        text="Deleted user"
        class="italic truncate"
      />
      <router-link
        v-else
        :event="currentUser.role.includes('admin') ? 'click' : ''"
        target="_blank"
        :to="{
          name: 'TeamSessionHistory',
          params: {
            user_id: entry.session.user.id,
            redirect_target: `/clients/${client.id}`,
            redirect_label: 'Client targets'
          }
        }"
      >
        <div
          :title="entry.session.user.name"
          class="flex items-center justify-center h-8 px-3 truncate border rounded bg-success-100 border-success-100 max-w-25"
          :class="[
            currentUser.role.includes('admin')
              ? 'hover:border-success-400'
              : 'cursor-default'
          ]"
        >
          <BlTextBody1
            class="truncate text-success"
            :text="entry.session.user.name"
          />
        </div>
      </router-link>
    </div>
    <!-- Comments section -->
    <div
      v-if="dataFormat !== 'distribution' && dataFormat !== 'cumulative'"
      class="relative flex items-center flex-grow flex-shrink-0 px-2 w-80 bg-pure-white"
    >
      <div
        v-if="!taskTarget"
        class="line-clamp-1"
        :id="`entry-comment-${entry.id}`"
      >
        <BlTextBody1 :text="entry.comment || '-'" />
      </div>
      <div
        :id="`entry-view-all-${entry.id}`"
        class="absolute flex right-2 bg-pure-white"
        :style="{ opacity: 0, zIndex: 1 }"
        @click="commentOpened = true"
      >
        <BlTextBody1 text="..." class="px-1" />
        <div
          class="flex items-center flex-shrink-0 px-2 text-sm font-bold rounded cursor-pointer text-light-purple hover:bg-light-purple-100"
        >
          View all
        </div>
      </div>
    </div>

    <!-- Menu -->
    <div
      v-if="dataFormat !== 'distribution'"
      class="relative flex items-center justify-center flex-shrink-0 w-12 px-2 ml-auto bg-pure-white"
    >
      <div id="entry-menu" class="relative">
        <BlInputButton
          @clicked="toggleMenu"
          icon="dots-vertical"
          class="flex items-center justify-center w-6 h-6 hover:bg-hover-state"
          type="plain"
        />
        <div
          v-if="menuOpened"
          class="absolute top-0 z-50 flex flex-col p-1 space-y-1 border rounded right-8 bg-pure-white border-grey-200"
        >
          <button
            @click.prevent="editOpened = true"
            class="px-4 py-2 text-left rounded outline-none hover:bg-hover-state w-44"
          >
            <BlTextBody2 class="text-slate-1000" text="Edit record" />
          </button>
          <router-link
            v-if="entry.session && entry.session.slug"
            :to="`/session/${entry.session.slug}`"
            class="px-4 py-2 text-left rounded outline-none hover:bg-hover-state w-44"
          >
            <BlTextBody2 class="text-slate-1000" text="See record on session" />
          </router-link>
          <button
            @click.prevent="deleteOpened = true"
            class="px-4 py-2 text-left rounded outline-none hover:bg-hover-state w-44"
          >
            <BlTextBody2 class="text-error-400" text="Delete record" />
          </button>
        </div>
      </div>
    </div>

    <!-- Modals/Backdrops -->
    <BlBackdrop
      v-if="commentOpened"
      title="Comments"
      label="Close"
      cancelLabel="Edit"
      @backdrop-close="
        commentOpened = false;
        editOpened = true;
      "
      @backdrop-action="commentOpened = false"
    >
      <div
        class="relative px-8 overflow-y-auto"
        :style="{ maxHeight: 'calc(100vh - 14rem)' }"
      >
        <div class="flex flex-col">
          <BlTextBody2
            :text="entry.comment"
            class="flex-wrap break-words w-136"
          />
        </div>
      </div>
    </BlBackdrop>

    <!-- Edit modal -->
    <BlBackdrop
      v-if="editOpened"
      title="Edit record"
      description="Editing the record will only affect the data in the table; it will not impact the data recorded during the actual sessions."
      label="Save"
      :disabled="submitLoading"
      @backdrop-close="onCancelEdit"
      @backdrop-action="onSave"
    >
      <div
        class="flex flex-col gap-4 overflow-y-auto w-160"
        :style="{ maxHeight: 'calc(100vh - 16rem)' }"
      >
        <div class="px-8 space-y-4">
          <div class="grid grid-cols-2 gap-4">
            <BlInputDate
              :input="recordedAtDatetInput"
              @changed="inputHandler"
            />
            <BlInputDate :input="recordedAtTimeInput" @changed="inputHandler" />
          </div>
          <BlInputText
            v-if="!taskTarget && dataFormat !== 'cumulative'"
            :input="commentInput"
            @changed="inputHandler"
          />
        </div>
        <div style="background-color: #F9FAFB;" class="w-full h-5"></div>
        <div class="px-8 space-y-3">
          <BlTextSubtitle1 text="Scores" />
          <div
            class="flex items-center gap-3 space-y-2 overflow-x-scroll custom-scrollbar"
            style="max-width: calc(100% - 2rem);"
          >
            <div
              v-for="task in getFilteredTasks()"
              :key="`task-${task.id}`"
              :id="'task-id-' + task.id"
            >
              <div
                class="flex flex-col items-center justify-center flex-shrink-0 w-20 p-3 rounded cursor-pointer bg-slate-200"
              >
                <BlTextBody1
                  :text="getAverageScore(task)"
                  class="font-bold text-dark-purple-100"
                />
                <BlTextBody1 :text="task.code" class="text-slate-700" />
              </div>
            </div>
            <div
              v-for="pb in problemBehaviors"
              :key="`pb-${pb.id}`"
              :id="'pb-id-' + pb.id"
            >
              <div
                class="flex flex-col items-center justify-center flex-shrink-0 p-3 rounded cursor-pointer w-18 bg-slate-200"
              >
                <BlTextBody1
                  :text="getRecordProblemBehaviors(pb.id)"
                  class="font-bold text-tomato-700"
                />
                <BlTextBody1 :text="pb.code" class="text-slate-700" />
              </div>
            </div>
          </div>
          <div
            v-if="!hasScores"
            class="flex items-center p-3 rounded bg-cornflower-200"
          >
            <div class="flex gap-2">
              <BlIcon icon="Info-blue" class="w-5 h-5" />
              <BlTextBody2
                text="No trials were recorded during the session, so the scores cannot be modified."
                class="text-cornflower-800"
              />
            </div>
          </div>
          <div v-else class="flex flex-col p-3 pr-3 rounded gap-7 bg-slate-200">
            <div
              v-for="score in getFilteredScores()"
              :key="score.key"
              class="flex items-center justify-between"
            >
              <div class="space-x-3">
                <BlTextBody2 :text="`${score.key}.`" class="text-slate-800" />
                <BlTextBody2 :text="score.task_code" class="text-slate-800" />
              </div>
              <div class="flex gap-2">
                <BlSelectBox
                  :options="editPromptsOptions"
                  class="w-48"
                  position="top"
                  v-model="score.prompt_id"
                  @change="value => onUpdateScores(value, 'prompt', score.key)"
                />
                <BlSelectBox
                  :options="
                    problemBehaviors.map(pb => ({
                      value: pb.id,
                      label: pb.code
                    }))
                  "
                  class="w-24"
                  placeholder="-"
                  position="top"
                  v-model="score.target_problem_behavior_id"
                  @change="
                    value =>
                      onUpdateScores(value, 'problem_behavior', score.key)
                  "
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </BlBackdrop>

    <!-- Delete confirm modal -->
    <BlBackdrop
      v-if="deleteOpened"
      title="Delete record"
      label="Delete"
      description="You are about to delete the record. Are you sure you want to proceed? This action is irreversible."
      type="error"
      empty
      @backdrop-close="deleteOpened = false"
      @backdrop-action="onRemove"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import moment from "moment";
import { getDateWithoutTimezone } from "@/plugins/helper.js";

import BlTextBody1 from "../../BlTextBody1.vue";
import BlInputButton from "../../BlInputButton.vue";
import BlInputText from "../../BlInputText.vue";
import BlInputTime from "../../BlInputTime.vue";
import BlBackdrop from "../../BlBackdrop.vue";
import BlIcon from "../../BlIcon.vue";
import BlInputDate from "../../BlInputDate.vue";
import BlTextBody2 from "../../BlTextBody2.vue";
import BlTextSubtitle1 from "../../BlTextSubtitle1.vue";
import BlSelectBox from "../../BlSelectBox.vue";

export default {
  name: "EntryPromptSbt",
  components: {
    BlTextBody1,
    BlInputButton,
    BlInputText,
    BlInputTime,
    BlBackdrop,
    BlIcon,
    BlInputDate,
    BlTextBody2,
    BlTextSubtitle1,
    BlSelectBox
  },
  props: [
    "promptColors",
    "tasks",
    "entry",
    "dataFormat",
    "problemBehaviors",
    "prompts",
    "taskTarget"
  ],
  data() {
    return {
      submitLoading: false,
      menuOpened: false,
      commentOpened: false,
      editOpened: false,
      deleteOpened: false,
      hoveredTaskId: null,
      problemBehaviorRecords: [],
      recordedAtDatetInput: {
        label: "Session date",
        model: moment(this.entry.recorded_at).format("YYYY-MM-DD"),
        asterisk: true
      },
      recordedAtTimeInput: {
        inputType: "time",
        label: "Session time",
        model: moment(this.entry.recorded_at).format("HH:mm"),
        asterisk: true
      },
      commentInput: {
        label: "Comment",
        placeholder: "Comment entry",
        model: this.entry.comment,
        textarea: true
      }
    };
  },
  watch: {
    taskTarget() {
      const viewAll = document.getElementById(
        `entry-view-all-${this.entry.id}`
      );
      viewAll.style.opacity = 0;

      this.$nextTick(() => {
        this.setupComment(this.entry.id);
      });
    }
  },
  updated() {
    this.$nextTick(() => {
      this.setupComment(this.entry.id);
    });
  },
  computed: {
    ...mapGetters(["client", "currentUser"]),
    editPromptsOptions() {
      return this.prompts.map(p => ({
        value: p.id,
        label: p.name
      }));
    },
    hasScores() {
      if (this.taskTarget) {
        return (
          this.entry.scores &&
          typeof this.entry.scores === "object" &&
          Object.keys(this.entry.scores).some(
            key => this.entry.scores[key].task_code === this.taskTarget.code
          )
        );
      }
      return (
        this.entry.scores &&
        typeof this.entry.scores === "object" &&
        Object.keys(this.entry.scores).length > 0
      );
    }
  },
  mounted() {
    this.setupComment(this.entry.id);
  },
  methods: {
    ...mapActions(["getTarget", "updateEntry", "deleteEntry"]),
    getDateWithoutTimezone,

    hoverDetail(taskId) {
      this.hoveredTaskId = taskId;
    },
    isTaskHovered(taskId) {
      return this.hoveredTaskId === taskId;
    },
    getAverageScore(task) {
      if (!this.entry.scores || typeof this.entry.scores !== "object") {
        return task.id ? "0" : {};
      }

      const scoreGroups = {};

      Object.values(this.entry.scores).forEach(score => {
        if (!score || typeof score !== "object") return;

        const taskId =
          score.target_task_id === task.id ? score.target_task_id : null;
        const code = score.task_code === task.code ? score.task_code : null;
        const id = taskId || code || null;
        if (!id) return;

        if (!scoreGroups[id]) {
          scoreGroups[id] = [];
        }
        scoreGroups[id].push(Number(score.prompt_score || 0));
      });

      const averages = {};
      Object.keys(scoreGroups).forEach(id => {
        if (scoreGroups[id].length === 0) {
          averages[id] = 0;
          return;
        }

        const totalScore = scoreGroups[id].reduce((acc, curr) => acc + curr, 0);
        averages[id] = Math.round(totalScore / scoreGroups[id].length);
      });
      if (task.id && averages[task.id] !== undefined) {
        const average = averages[task.id] || 0;
        return average > 0 ? `${average}%` : "0%";
      } else if (task.code && averages[task.code] !== undefined) {
        const average = averages[task.code] || 0;
        return average > 0 ? `${average}%` : "0%";
      }

      return Object.keys(averages).length === 0 ? "0%" : averages || "0%";
    },
    hasTaskProblemBehavior(taskId, pbId) {
      if (typeof this.entry.scores !== "object" || this.entry.scores === null) {
        return false;
      }

      const pbCode = this.problemBehaviors.find(pb => pb.id === pbId);
      if (!pbCode.code) return false;

      return Object.values(this.entry.scores).some(
        score =>
          score.target_task_id === taskId &&
          score.problem_behavior_code === pbCode.code &&
          score.target_problem_behavior_id === pbId
      );
    },
    getTaskProblemBehaviorCount(taskId, pbId) {
      if (typeof this.entry.scores !== "object" || this.entry.scores === null) {
        return "";
      }

      const pbCode = this.problemBehaviors.find(pb => pb.id === pbId);
      if (!pbCode.code) return "";

      let count = 0;
      Object.values(this.entry.scores).forEach(score => {
        if (
          score.target_task_id === taskId &&
          score.problem_behavior_code === pbCode.code &&
          score.target_problem_behavior_id === pbId
        ) {
          count++;
        }
      });
      return count > 0 ? count : "";
    },
    getRecordProblemBehaviors(pbId) {
      if (typeof this.entry.scores === "object" && this.entry.scores !== null) {
        let total = 0;

        const pbCode = this.problemBehaviors.find(pb => pb.id === pbId);

        if (!pbCode.code) return 0;

        Object.values(this.entry.scores).forEach(score => {
          if (this.taskTarget && score.target_task_id !== this.taskTarget.id) {
            return;
          }

          if (
            score.problem_behavior_code === pbCode.code &&
            score.target_problem_behavior_id === pbId
          ) {
            total++;
          }
        });

        if (!this.problemBehaviorRecords) {
          this.problemBehaviorRecords = [];
        }

        const existingIndex = this.problemBehaviorRecords.findIndex(
          r => r.code === pbCode.code
        );
        if (existingIndex >= 0) {
          this.problemBehaviorRecords[existingIndex].total = total;
        } else {
          this.problemBehaviorRecords.push({ code: pbCode.code, total });
        }

        return total;
      }

      return 0;
    },
    toggleMenu() {
      this.menuOpened = !this.menuOpened;
      if (this.menuOpened) {
        setTimeout(() => {
          document.addEventListener("click", this.closeHandler);
        }, 10);
      }
    },

    setupComment(entryId) {
      setTimeout(() => {
        const content = document.getElementById(`entry-comment-${entryId}`);
        const viewAll = document.getElementById(`entry-view-all-${entryId}`);

        if (content && viewAll) {
          if (this.taskTarget) {
            viewAll.style.opacity = 0;
          } else {
            viewAll.style.opacity =
              content.scrollHeight > content.clientHeight ? 1 : 0;
          }
        }
      }, 100);
    },

    inputHandler(payload) {
      payload.input.model = payload.value;
    },

    onUpdateScores(id, type, key) {
      if (type === "problem_behavior") {
        const pb = this.problemBehaviors.find(pb => pb.id === id);
        const oldScore = this.entry.scores[key];
        this.entry.scores[key] = {
          ...oldScore,
          problem_behavior_code: pb.code,
          target_problem_behavior_id: id,
          problem_behavior_id: pb.problem_behavior_id
        };
        return;
      }
      const prompt = this.prompts.find(p => p.id === id);
      const oldScore = this.entry.scores[key];
      this.entry.scores[key] = {
        ...oldScore,
        prompt_id: id,
        prompt_code: prompt.abbreviation,
        prompt_score: prompt.score
      };
    },

    getFilteredTasks() {
      if (this.taskTarget) {
        return this.tasks.filter(task => task.id === this.taskTarget.id);
      }
      return this.tasks;
    },

    getFilteredScores() {
      if (this.taskTarget) {
        return Object.values(this.entry.scores).filter(
          score => score.target_task_id === this.taskTarget.id
        );
      }
      return Object.values(this.entry.scores);
    },

    onCancelEdit() {
      this.submitLoading = true;
      this.getTarget({ id: this.entry.target_id }).then(() => {
        this.submitLoading = false;
        this.editOpened = false;
      });
    },

    selectTask(task) {
      this.hoveredTaskId = null;
      this.$emit("select-task", task);
    },

    onSave() {
      this.submitLoading = true;
      const recordedAt = moment(
        `${this.recordedAtDatetInput.model} ${this.recordedAtTimeInput.model}`
      ).format();

      const data = {
        id: this.entry.id,
        target_id: this.entry.target_id,
        recorded_at: recordedAt,
        comment: this.commentInput.model,
        scores: {}
      };

      const scores = Object.values(this.entry.scores);
      scores.forEach(score => {
        // Preserve the original score object
        data.scores[score.key] = {
          // Include all properties
          ...score,
          // You might want to ensure these are up to date
          prompt_id: score.prompt_id,
          prompt_code: score.prompt_code,
          prompt_score: score.prompt_score,
          problem_behavior_code: score.problem_behavior_code,
          problem_behavior_id: score.problem_behavior_id,
          target_problem_behavior_id: score.target_problem_behavior_id,
          target_task_id: score.target_task_id,
          task_code: score.task_code,
          task_color: score.task_color
        };
      });

      this.updateEntry(data).then(() => {
        this.submitLoading = false;
        this.editOpened = false;
      });
    },

    onRemove() {
      this.deleteEntry(this.entry).then(() => {
        this.deleteOpened = false;
      });
    },

    closeHandler() {
      document.removeEventListener("click", this.closeHandler);
      this.menuOpened = false;
    }
  }
};
</script>
<style>
.custom-scrollbar::-webkit-scrollbar {
  height: 9px;
  width: 12px;
  cursor: pointer;
}
</style>



// WEBPACK FOOTER //
// src/components/clients/target/EntryPromptSbt.vue